

import { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Image,
  X,
  Star,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import ImageUpload from "../components/ImageUpload";
import { galleriesApi, imageApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { getImageUrl } from "@/lib/utils";

interface Gallery {
  id: number;
  title: string;
  description: string;
  images: string[];
  is_active: boolean;
  is_selected: boolean;
  sort_order: number;
  created_at?: string;
}

function StatBadge({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div className={`px-3 py-1.5 rounded-lg ${color} flex items-center gap-2`}>
      <span className="text-xs font-medium opacity-80">{label}:</span>
      <span className="text-sm font-bold">{value}</span>
    </div>
  );
}

function GalleryForm({
  initialData,
  onSubmit,
  onCancel,
}: {
  initialData?: Partial<Gallery>;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const { showToast } = useToast();
  const [title, setTitle] = useState(initialData?.title || "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [images, setImages] = useState<string[]>(initialData?.images || []);
  const [is_active, setIsActive] = useState(initialData?.is_active ?? true);
  const [is_selected, setIsSelected] = useState(initialData?.is_selected ?? false);
  const [sort_order, setSortOrder] = useState(initialData?.sort_order || 0);

  const handleUpload = async (file: File) => {
    try {
      const result = await imageApi.upload(file, 'gallery');
      if (result && result.success) {
        const imageUrl = result.data?.url || result.url;
        return { success: true, url: imageUrl };
      }
      return { success: false, message: result?.message || 'Upload failed' };
    } catch (error: any) {
      return { success: false, message: error.message || 'Upload failed' };
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      title,
      description,
      images,
      is_active,
      is_selected,
      sort_order: Number(sort_order),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Title *</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Wedding Gallery"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          required
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the gallery..."
          rows={2}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Images</label>
        <ImageUpload
          images={images}
          onImagesChange={setImages}
          maxImages={50}
          uploadFn={handleUpload}
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Sort Order</label>
          <input
            type="number"
            value={sort_order}
            onChange={(e) => setSortOrder(Number(e.target.value))}
            min="0"
            className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Active</label>
          <button
            type="button"
            onClick={() => setIsActive(!is_active)}
            className={`w-full px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
              is_active ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-stone-50 border-stone-200 text-stone-500"
            }`}
          >
            {is_active ? "Yes" : "No"}
          </button>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Homepage</label>
          <button
            type="button"
            onClick={() => setIsSelected(!is_selected)}
            className={`w-full px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
              is_selected ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-stone-50 border-stone-200 text-stone-500"
            }`}
          >
            {is_selected ? "Yes" : "No"}
          </button>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData?.id ? "Update" : "Add"} Gallery
        </button>
      </div>
    </form>
  );
}

export default function GalleriesManager() {
  const { showToast } = useToast();

  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGallery, setEditingGallery] = useState<Gallery | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingGallery, setDeletingGallery] = useState<Gallery | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await galleriesApi.list();
      console.log('Galleries API response:', response);
      
      // Handle different response structures
      let data = [];
      if (response.data?.data?.data) {
        data = response.data.data.data;
      } else if (response.data?.data) {
        data = response.data.data;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      }
      
      console.log('Extracted galleries:', data);
      setGalleries(data.sort((a: Gallery, b: Gallery) => (a.sort_order || 0) - (b.sort_order || 0)));
    } catch (error) {
      console.error('Failed to load galleries:', error);
      showToast("Failed to load galleries", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredGalleries = useMemo(() => {
    if (!searchQuery) return galleries;
    const q = searchQuery.toLowerCase();
    return galleries.filter(
      (g) => g.title.toLowerCase().includes(q) || g.description?.toLowerCase().includes(q)
    );
  }, [galleries, searchQuery]);

  const stats = useMemo(
    () => ({
      total: galleries.length,
      active: galleries.filter((g) => g.is_active).length,
      selected: galleries.filter((g) => g.is_selected).length,
    }),
    [galleries]
  );

  const moveItem = async (gallery: Gallery, direction: 'up' | 'down') => {
    const currentIndex = galleries.findIndex(g => g.id === gallery.id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= galleries.length) return;
    
    const newGalleries = [...galleries];
    [newGalleries[currentIndex], newGalleries[targetIndex]] = [newGalleries[targetIndex], newGalleries[currentIndex]];
    
    newGalleries.forEach((g, idx) => {
      g.sort_order = idx;
    });
    
    setGalleries(newGalleries);
    
    setSavingOrder(true);
    try {
      const orderData = newGalleries.map(g => ({ id: g.id, sort_order: g.sort_order }));
      await galleriesApi.updateOrder(orderData);
      showToast("Order updated", "success");
    } catch (error) {
      showToast("Failed to update order", "error");
      loadData();
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingGallery) return;
    setDeleteLoading(true);
    try {
      await galleriesApi.delete(deletingGallery.id);
      setGalleries((p) => p.filter((g) => g.id !== deletingGallery.id));
      showToast("Gallery deleted successfully", "success");
      setDeleteModalOpen(false);
      setDeletingGallery(null);
    } catch (error) {
      showToast("Failed to delete gallery", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (data: any) => {
    setIsModalOpen(false);
    setEditingGallery(null);
    try {
      if (editingGallery) {
        await galleriesApi.update(editingGallery.id, data);
        showToast("Gallery updated successfully", "success");
        loadData();
      } else {
        await galleriesApi.create(data);
        showToast("Gallery created successfully", "success");
        loadData();
      }
    } catch (error) {
      showToast("Failed to save gallery", "error");
    }
  };

  const handleToggleSelected = async (gallery: Gallery) => {
    try {
      await galleriesApi.toggleSelected(gallery.id);
      setGalleries((p) =>
        p.map((g) => (g.id === gallery.id ? { ...g, is_selected: !g.is_selected } : g))
      );
      showToast("Selection updated", "success");
    } catch (error) {
      showToast("Failed to update selection", "error");
    }
  };

  const handleToggleActive = async (gallery: Gallery) => {
    try {
      await galleriesApi.toggleActive(gallery.id);
      setGalleries((p) =>
        p.map((g) => (g.id === gallery.id ? { ...g, is_active: !g.is_active } : g))
      );
      showToast("Status updated", "success");
    } catch (error) {
      showToast("Failed to update status", "error");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Compact Stats */}
      <div className="flex flex-wrap gap-2 mb-4">
        <StatBadge label="Total" value={stats.total} color="bg-stone-100 text-stone-700" />
        <StatBadge label="Active" value={stats.active} color="bg-emerald-100 text-emerald-700" />
        <StatBadge label="Homepage" value={stats.selected} color="bg-amber-100 text-amber-700" />
      </div>

      {/* Search and Add */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search galleries..."
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
        <button
          onClick={() => {
            setEditingGallery(null);
            setIsModalOpen(true);
          }}
          className="px-4 py-2 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Gallery
        </button>
      </div>

      {savingOrder && (
        <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-center gap-2">
          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Saving order...
        </div>
      )}

      {filteredGalleries.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Image className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No galleries found</p>
        </div>
      ) : (
        <>
          {/* Mobile Card Layout */}
          <div className="sm:hidden space-y-2">
            {filteredGalleries.map((gallery, index) => (
              <div key={gallery.id} className="bg-white rounded-xl border border-stone-200 p-3">
                <div className="flex items-start gap-3">
                  {gallery.images?.[0] && (
                    <img src={getImageUrl(gallery.images[0])} alt="" className="w-14 h-14 rounded-lg object-cover border border-stone-200 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-stone-800 text-sm truncate">{gallery.title}</h3>
                      <span className="text-xs text-stone-400">#{gallery.sort_order}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-stone-500">{gallery.images?.length || 0} images</span>
                      <button
                        onClick={() => handleToggleActive(gallery)}
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          gallery.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"
                        }`}
                      >
                        {gallery.is_active ? "Active" : "Inactive"}
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => handleToggleSelected(gallery)}
                        className={`p-1 rounded ${gallery.is_selected ? "text-amber-500" : "text-stone-300"}`}
                      >
                        <Star className={`w-4 h-4 ${gallery.is_selected ? "fill-current" : ""}`} />
                      </button>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => moveItem(gallery, 'up')}
                          disabled={index === 0}
                          className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100 disabled:opacity-30"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => moveItem(gallery, 'down')}
                          disabled={index === filteredGalleries.length - 1}
                          className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100 disabled:opacity-30"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setEditingGallery(gallery); setIsModalOpen(true); }}
                          className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setDeletingGallery(gallery); setDeleteModalOpen(true); }}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table Layout */}
          <div className="hidden sm:block bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="w-16 px-3 py-2 text-center text-xs font-semibold text-stone-500">Order</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500">Title</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-stone-500">Images</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-stone-500">Status</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-stone-500">Homepage</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-stone-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredGalleries.map((gallery, index) => (
                    <tr key={gallery.id} className="hover:bg-stone-50 transition-colors">
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => moveItem(gallery, 'up')}
                            disabled={index === 0}
                            className="p-1 rounded text-stone-400 hover:bg-stone-200 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <span className="text-xs text-stone-500 w-5 text-center">{gallery.sort_order}</span>
                          <button
                            onClick={() => moveItem(gallery, 'down')}
                            disabled={index === filteredGalleries.length - 1}
                            className="p-1 rounded text-stone-400 hover:bg-stone-200 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {gallery.images?.[0] && (
                            <img src={getImageUrl(gallery.images[0])} alt="" className="w-8 h-8 rounded object-cover border border-stone-200" />
                          )}
                          <div>
                            <p className="font-medium text-stone-800 text-sm">{gallery.title}</p>
                            {gallery.description && (
                              <p className="text-xs text-stone-400 truncate max-w-[200px]">{gallery.description}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="text-sm text-stone-600">{gallery.images?.length || 0}</span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => handleToggleActive(gallery)}
                          className={`px-2 py-1 rounded-lg text-xs font-medium ${
                            gallery.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"
                          }`}
                        >
                          {gallery.is_active ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => handleToggleSelected(gallery)}
                          className={`p-1 rounded transition-colors ${
                            gallery.is_selected ? "text-amber-500" : "text-stone-300"
                          }`}
                          title={gallery.is_selected ? "Remove from homepage" : "Add to homepage"}
                        >
                          <Star className={`w-4 h-4 ${gallery.is_selected ? "fill-current" : ""}`} />
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => { setEditingGallery(gallery); setIsModalOpen(true); }}
                            className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => { setDeletingGallery(gallery); setDeleteModalOpen(true); }}
                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingGallery(null);
        }}
        title={editingGallery ? "Edit Gallery" : "Add New Gallery"}
        size="md"
      >
        <GalleryForm
          initialData={editingGallery || undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingGallery(null);
          }}
        />
      </Modal>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingGallery(null);
        }}
        onConfirm={handleDelete}
        title="Delete Gallery"
        message="Are you sure you want to delete this gallery? This action cannot be undone."
        itemName={deletingGallery?.title}
        loading={deleteLoading}
      />
    </div>
  );
}
